import { useRef, useState } from "react";
import "../styles/TagInputPanel.css";

/**
 * Pure presentational tag panel — no API calls.
 *
 * Props:
 *   title          – panel heading (default "Tags")
 *   tags           – array of { id, label } — currently selected tags
 *   suggestions    – array of { id, label } — typeahead options from parent
 *   onAdd(label)   – parent resolves/creates the tag; called with raw typed string
 *   onRemove(id)   – parent removes the tag mapping
 *   onClose()      – optional; renders × in header when provided
 *   onQueryChange  – parent drives the typeahead (receives query string)
 *   placeholder    – input placeholder (default "Add a tag and press Enter")
 *   canEdit        – show input + remove buttons (default true)
 *   busy           – disable input while saving
 */
export default function TagInputPanel({
  title = "Tags",
  tags = [],
  suggestions = [],
  onAdd,
  onRemove,
  onClose,
  onQueryChange,
  placeholder = "Add a tag and press Enter",
  canEdit = true,
  busy = false,
}) {
  const [query, setQuery] = useState("");
  const [dropOpen, setDropOpen] = useState(false);
  const inputRef = useRef(null);

  const commit = (raw) => {
    const v = String(raw || "").trim();
    if (!v) return;
    onAdd?.(v);
    setQuery("");
    onQueryChange?.("");
    setDropOpen(false);
  };

  const handleChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    onQueryChange?.(v);
    setDropOpen(v.trim().length > 0);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commit(query);
    }
    if (e.key === "Escape") {
      setDropOpen(false);
      setQuery("");
      onQueryChange?.("");
    }
  };

  const pickSuggestion = (s) => {
    onAdd?.(s.label);
    setQuery("");
    onQueryChange?.("");
    setDropOpen(false);
    inputRef.current?.focus();
  };

  return (
    <div className="cmn-tip-panel">
      {/* ── header ── */}
      <div className="cmn-tip-header">
        <span className="cmn-tip-title">{title}</span>
        {onClose && (
          <button
            type="button"
            className="cmn-tip-close"
            onClick={onClose}
            aria-label="Close tags panel"
          >×</button>
        )}
      </div>

      {/* ── chips — only rendered when there are tags ── */}
      {(tags.length > 0 || (!canEdit)) && (
      <div className="cmn-tip-chips">
        {tags.length === 0 && !canEdit && (
          <span className="cmn-tip-empty">No tags.</span>
        )}
        {tags.map((t) => (
          <span key={t.id} className="cmn-tip-chip">
            <span className="cmn-tip-chip-label">{t.label}</span>
            {canEdit && (
              <button
                type="button"
                className="cmn-tip-chip-x"
                onClick={() => onRemove?.(t.id)}
                disabled={busy}
                aria-label={`Remove ${t.label}`}
              >
                ×
              </button>
            )}
          </span>
        ))}
      </div>
      )}

      {/* ── input ── */}
      {canEdit && (
        <div className="cmn-tip-input-wrap">
          <span className="cmn-tip-plus" aria-hidden="true">+</span>
          <input
            ref={inputRef}
            className="cmn-tip-input"
            value={query}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onBlur={() => setTimeout(() => setDropOpen(false), 150)}
            placeholder={placeholder}
            disabled={busy}
            autoComplete="off"
            spellCheck={false}
          />
          {dropOpen && suggestions.length > 0 && (
            <div className="cmn-tip-drop">
              {suggestions.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  className="cmn-tip-drop-opt"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pickSuggestion(s)}
                >
                  {s.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
